import { useEffect, useRef } from 'react'
import { useCopyPrompt } from '../hooks/useCopyPrompt'

/**
 * Tints the OpenDeploy-specific tokens inside a prompt. The full /start URL
 * is matched first so its inner "/opendeploy" never matches on its own.
 */
const HIGHLIGHT_PATTERN =
  /(https:\/\/opendeploy\.dev\/start|opendeploy\.dev\/start|\/opendeploy)/

export function renderPromptText(text: string) {
  return text
    .split(HIGHLIGHT_PATTERN)
    .map((part, index) =>
      index % 2 === 1 ? (
        <span key={index} className="od-tpl-hl">
          {part}
        </span>
      ) : (
        part
      ),
    )
}

/**
 * Splits a prompt that uses the "Step N — …" convention into its individual
 * steps (label prefix stripped — the box shows its own number). A prompt that
 * does not use the convention is returned as a single step, so the block
 * degrades to one box.
 */
export function splitSteps(prompt: string): string[] {
  const trimmed = prompt.trim()
  if (!/^Step\s+\d+\s*[—-]/.test(trimmed)) return [trimmed]
  return trimmed
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => chunk.replace(/^Step\s+\d+\s*[—-]\s*/, ''))
}

/** One step, in its own box, with its own copy button. */
function StepBox({ index, text }: { index: number; text: string }) {
  const { copied, copyFailed, copy } = useCopyPrompt()
  const textRef = useRef<HTMLSpanElement>(null)

  // Total copy failure: select this step's text so "press ⌘C" works.
  useEffect(() => {
    if (!copyFailed) return
    const node = textRef.current
    const selection = window.getSelection()
    if (!node || !selection) return

    const range = document.createRange()
    range.selectNodeContents(node)
    selection.removeAllRanges()
    selection.addRange(range)
  }, [copyFailed])

  return (
    <li className="od-tpl-step-box">
      <span className="od-tpl-step-box-num" aria-hidden="true">
        {index + 1}
      </span>
      <span ref={textRef} className="od-tpl-step-box-text">
        {renderPromptText(text)}
      </span>
      <button
        type="button"
        className="od-tpl-prompt-copy od-tpl-step-copy"
        aria-label={`Copy step ${index + 1}`}
        onClick={() => copy(text)}
      >
        {copyFailed ? 'press ⌘C' : copied ? 'copied' : 'copy'}
      </button>
      <span className="v2-sr-only" aria-live="polite">
        {copied ? 'Prompt copied' : ''}
      </span>
    </li>
  )
}

/**
 * The template prompt surface: a label, then the prompt rendered as one box
 * per step — each step copyable on its own.
 */
export default function PromptBlock({ slug, prompt }: { slug: string; prompt: string }) {
  const steps = splitSteps(prompt)

  return (
    <div className="od-tpl-promptblock">
      <div className="od-tpl-prompt-bar">
        <span className="od-tpl-prompt-label">{slug} · prompt</span>
      </div>

      <ol className="od-tpl-steplist">
        {steps.map((text, index) => (
          <StepBox key={index} index={index} text={text} />
        ))}
      </ol>
    </div>
  )
}
